import {
  getFocusedCommentContentId,
  useDatabaseContent,
} from '@databases/database-state';
import { defaultStoreUtils } from '@databases/redux';
import { isBrokenDatabase } from '@databases/utils/database/utils';
import { selectAllVisibleUnresolvedThreads } from '@databases/utils/redux/comments/selectors';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import React, { createContext, useEffect, useMemo, useState } from 'react';

import { fetchParentThreadIdFromCommentContentId } from '../fetch-parent-comment';

export type OpenThreadContextData = {
  valueId: string | undefined;
  threadId?: string | undefined;
  create?: boolean;
  panel?: boolean;
};

export type Thread =
  | {
      threadId: string;
      isResolved: boolean;
      commentCount: number;
      versionNumber: number;
    }
  | undefined;

export type ThreadWithValueId = (Thread & { valueId: string }) | undefined;

export interface CommentContextInterface {
  openThread: OpenThreadContextData;
  setOpenThread: (value: OpenThreadContextData) => void;
  focusedCommentThreadId: string | undefined;
  setFocusedCommentThreadId: (threadId: string | undefined) => void;
  allThreads: ThreadWithValueId[];
}

export const CommentContext = createContext<CommentContextInterface>({
  openThread: { valueId: undefined },
  setOpenThread: () => {},
  focusedCommentThreadId: undefined,
  setFocusedCommentThreadId: () => {},
  allThreads: [],
});

export const CommentProvider = ({ children }: { children: React.ReactNode }) => {
  const [openThread, setOpenThread] = useState<OpenThreadContextData>({
    valueId: undefined,
  });
  const [focusedCommentThreadId, setFocusedCommentThreadId] = useState<
    string | undefined
  >(getFocusedCommentContentId());
  const { contentId } = useDatabaseContent();
  const store = defaultStoreUtils.useStore();

  useEffect(() => {
    if (!contentId) {
      return;
    }

    const focusedCommentContentIdFromParam = getFocusedCommentContentId();

    if (focusedCommentContentIdFromParam) {
      fetchParentThreadIdFromCommentContentId(
        contentId,
        focusedCommentContentIdFromParam
      ).then((threadId) => {
        setFocusedCommentThreadId(threadId);
      });
    }
  }, [contentId]);

  const commentThreads = defaultStoreUtils.useEqualSelector((state) => {
    if (!contentId) {
      return undefined;
    }
    const database = selectDatabaseByContentIdOptional(state, { contentId });

    if (!database || isBrokenDatabase(database)) {
      return undefined;
    }

    return database.commentThreads;
  });

  const allThreads = useMemo(() => {
    if (!contentId || !commentThreads) {
      return [];
    }
    return selectAllVisibleUnresolvedThreads(store.getState(), { contentId });
  }, [contentId, commentThreads, store]);

  return (
    <CommentContext.Provider
      value={{
        openThread,
        setOpenThread,
        focusedCommentThreadId,
        setFocusedCommentThreadId,
        allThreads,
      }}
    >
      {children}
    </CommentContext.Provider>
  );
};
